import React, { useRef, useImperativeHandle, forwardRef, useEffect } from 'react';

const NUM_POINTS = 12;
const NUM_PATHS = 2;
const DELAY_POINTS_MAX = 0.14;
const DELAY_PER_PATH = 0.08;
const DURATION = 0.55;

function easeInOut(t: number): number {
  // ease-in-out cubic — courbe plus smooth qu'une quadratique
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function buildPath(points: number[], filled: boolean): string {
  let d = filled ? `M 0 0 V ${points[0]} C` : `M 0 ${points[0]} C`;
  for (let j = 0; j < NUM_POINTS - 1; j++) {
    const p = ((j + 1) / (NUM_POINTS - 1)) * 100;
    const cp = p - (100 / (NUM_POINTS - 1)) / 2;
    d += ` ${cp} ${points[j]} ${cp} ${points[j + 1]} ${p} ${points[j + 1]}`;
  }
  d += filled ? ` V 100 H 0` : ` V 0 H 0`;
  return d;
}

export interface PageTransitionHandle {
  cover(): Promise<void>;
  reveal(): Promise<void>;
}

const PageTransitionOverlay = forwardRef<PageTransitionHandle>(function PageTransitionOverlay(_, ref) {
  const pathRefs = useRef<(SVGPathElement | null)[]>([null, null]);
  const allPoints = useRef<number[][]>(
    Array.from({ length: NUM_PATHS }, () => Array(NUM_POINTS).fill(0))
  );
  const rafId = useRef<number>(0);

  const renderPaths = (filled: boolean) => {
    for (let i = 0; i < NUM_PATHS; i++) {
      pathRefs.current[i]?.setAttribute('d', buildPath(allPoints.current[i], filled));
    }
  };

  useEffect(() => {
    renderPaths(false);
  }, []);

  const animateToZero = (filled: boolean): Promise<void> => {
    cancelAnimationFrame(rafId.current);

    for (let i = 0; i < NUM_PATHS; i++) {
      allPoints.current[i] = Array(NUM_POINTS).fill(100);
    }
    renderPaths(filled);

    const pointsDelay = Array.from({ length: NUM_POINTS }, () => Math.random() * DELAY_POINTS_MAX);
    const startTime = performance.now();

    return new Promise((resolve) => {
      const tick = () => {
        const elapsed = (performance.now() - startTime) / 1000;
        let allDone = true;

        for (let i = 0; i < NUM_PATHS; i++) {
          const pathDelay = filled
            ? DELAY_PER_PATH * i
            : DELAY_PER_PATH * (NUM_PATHS - i - 1);

          for (let j = 0; j < NUM_POINTS; j++) {
            const t = Math.max(0, (elapsed - pointsDelay[j] - pathDelay) / DURATION);
            if (t < 1) allDone = false;
            allPoints.current[i][j] = 100 * (1 - easeInOut(Math.min(t, 1)));
          }
        }

        renderPaths(filled);

        if (!allDone) {
          rafId.current = requestAnimationFrame(tick);
        } else {
          for (let i = 0; i < NUM_PATHS; i++) {
            allPoints.current[i] = Array(NUM_POINTS).fill(0);
          }
          renderPaths(filled);
          resolve();
        }
      };

      rafId.current = requestAnimationFrame(tick);
    });
  };

  useImperativeHandle(ref, () => ({
    cover: () => animateToZero(true),
    reveal: () => animateToZero(false),
  }));

  return (
    <svg
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 9999 }}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pt-dark" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a1b24" />
          <stop offset="100%" stopColor="#0d0e15" />
        </linearGradient>
        <linearGradient id="pt-yellow" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#facc15" />
          <stop offset="100%" stopColor="#f97316" />
        </linearGradient>
      </defs>
      <path ref={(el) => { pathRefs.current[0] = el; }} fill="url(#pt-dark)" />
      <path ref={(el) => { pathRefs.current[1] = el; }} fill="url(#pt-yellow)" />
    </svg>
  );
});

export default PageTransitionOverlay;
